import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { CalendarEvent } from '@/types';

interface TimeVaultDB extends DBSchema {
  events: {
    key: string;
    value: CalendarEvent;
    indexes: {
      'by-date': string;
    };
  };
}

const DB_NAME = 'timevault-calendar';
const DB_VERSION = 1;

let dbInstance: IDBPDatabase<TimeVaultDB> | null = null;

async function getDB(): Promise<IDBPDatabase<TimeVaultDB>> {
  if (dbInstance) return dbInstance;

  dbInstance = await openDB<TimeVaultDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      const store = db.createObjectStore('events', { keyPath: 'id' });
      store.createIndex('by-date', 'date');
    },
  });

  return dbInstance;
}

export async function getAllEvents(): Promise<CalendarEvent[]> {
  const db = await getDB();
  return db.getAll('events');
}

export async function getEventsByDate(date: string): Promise<CalendarEvent[]> {
  const db = await getDB();
  return db.getAllFromIndex('events', 'by-date', date);
}

export async function getEventsByMonth(year: number, month: number): Promise<CalendarEvent[]> {
  const db = await getDB();
  const all = await db.getAll('events');
  return all.filter((e) => {
    const d = new Date(e.date);
    return d.getFullYear() === year && d.getMonth() === month;
  });
}

export async function addEvent(event: CalendarEvent): Promise<void> {
  const db = await getDB();
  await db.put('events', event);
}

export async function updateEvent(event: CalendarEvent): Promise<void> {
  const db = await getDB();
  await db.put('events', { ...event, updatedAt: Date.now() });
}

export async function deleteEvent(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('events', id);
}

export async function exportAllEvents(): Promise<string> {
  const events = await getAllEvents();
  return JSON.stringify(events, null, 2);
}

export async function importEvents(json: string): Promise<number> {
  const events: CalendarEvent[] = JSON.parse(json);
  const db = await getDB();
  const tx = db.transaction('events', 'readwrite');
  for (const event of events) {
    await tx.store.put(event);
  }
  await tx.done;
  return events.length;
}
